/**
 * Locale segment in URL (first path segment). Must match supported UI languages.
 * @see loadTranslations.js — keep in sync with supportedLangs
 */
import { defaultLang, supportedLangs } from "./loadTranslations";

export const SUPPORTED_LOCALES = supportedLangs;
export const DEFAULT_LOCALE = defaultLang;
export const LOCALE_STORAGE_KEY = "prombiz_locale";
/** Legacy key used by I18nContext — kept in sync from URL */
export const LANG_STORAGE_KEY = "prombiz_lang";

export function isSupportedLocale(seg) {
  return typeof seg === "string" && SUPPORTED_LOCALES.includes(seg.toLowerCase());
}

export function readStoredLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const a = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (a && isSupportedLocale(a)) return a.toLowerCase();
    const b = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (b && isSupportedLocale(b)) return b.toLowerCase();
  } catch {
    /* ignore */
  }
  return DEFAULT_LOCALE;
}

export function persistLocale(locale) {
  if (typeof window === "undefined" || !isSupportedLocale(locale)) return;
  const l = locale.toLowerCase();
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, l);
    window.localStorage.setItem(LANG_STORAGE_KEY, l);
  } catch {
    /* ignore */
  }
}

/**
 * Strip leading /en|ru|he from pathname.
 * @returns {{ locale: string|null, pathnameWithoutLocale: string }}
 */
export function parseLocaleFromPathname(pathname) {
  if (!pathname || pathname === "/") {
    return { locale: null, pathnameWithoutLocale: "/" };
  }
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 0) return { locale: null, pathnameWithoutLocale: "/" };
  const first = segs[0].toLowerCase();
  if (isSupportedLocale(first)) {
    const rest = segs.slice(1).join("/");
    return {
      locale: first,
      pathnameWithoutLocale: rest ? `/${rest}` : "/",
    };
  }
  return { locale: null, pathnameWithoutLocale: pathname.startsWith("/") ? pathname : `/${pathname}` };
}

/**
 * Negotiate default locale (root redirect) — browser + storage.
 */
export function negotiateLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const stored = readStoredLocale();
  if (stored && stored !== DEFAULT_LOCALE) return stored;
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  if (isSupportedLocale(nav)) return nav;
  return DEFAULT_LOCALE;
}

/**
 * Prefix an app path with locale. If already prefixed or external / __dev, unchanged.
 * @param {string} path - e.g. /login or login
 * @param {string} locale
 */
export function prefixPathWithLocale(path, locale) {
  if (typeof path !== "string") return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/__dev")) return path;
  const loc = isSupportedLocale(locale) ? locale.toLowerCase() : DEFAULT_LOCALE;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized === "/") return `/${loc}`;
  const segs = normalized.split("/").filter(Boolean);
  if (segs.length && isSupportedLocale(segs[0])) return normalized;
  return `/${loc}${normalized}`;
}

/**
 * Swap locale in current pathname; preserves path after locale.
 */
export function swapLocaleInPathname(pathname, newLocale) {
  if (!isSupportedLocale(newLocale)) return pathname;
  const { pathnameWithoutLocale } = parseLocaleFromPathname(pathname || "/");
  return prefixPathWithLocale(pathnameWithoutLocale === "/" ? "/" : pathnameWithoutLocale, newLocale);
}
